import {css, useTheme} from '@mui/material';
import * as React from 'react';
import {mixins} from 'src/js/common/jsStyles/mixins';
import {GetStylesFunction} from 'src/js/common/types';
import {HierarchyTreeNode as HierarchyTreeNodeModel} from 'src/js/model/hierarchy_tree_children';

import {HierarchyNodeItemLabel} from './HierarchyNodeItemLabel';

interface OwnProps {
  node: HierarchyTreeNodeModel;
  onNodeClick?: (node: HierarchyTreeNodeModel) => void;
  tabIndex?: number;
  isItemHighlighted?: boolean;
  getOptionDisabled?: (row: any) => boolean;
}

type Props = OwnProps;

export const HierarchyNodeItem: React.FC<Props> = (props) => {
  const {
    node,
    onNodeClick,
    tabIndex = 0,
    isItemHighlighted,
    getOptionDisabled,
  } = props;
  const theme = useTheme();
  const styles = getStyles({theme});
  const isDisabled = React.useMemo(
    () =>
      getOptionDisabled &&
      getOptionDisabled({
        id: node.getId(),
        label: node.getLabel(),
        levelType: node.getLevelType(),
      }),
    [getOptionDisabled, node]
  );

  const openFile = () => {
    if (onNodeClick && !isDisabled) {
      onNodeClick(node);
    }
  };

  const handleClick: React.MouseEventHandler<HTMLElement> = (event) => {
    if (event) {
      event.stopPropagation();
    }
    openFile();
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLElement> = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation();
      openFile();
    }
    return true;
  };

  return (
    <span
      data-name={'hierarchy-node-item--button-wrapper'}
      css={styles.dlHierarchyNodeItem}
    >
      <button
        type={'button'}
        css={
          isItemHighlighted && !isDisabled
            ? styles.highlightedItem
            : styles.dlHierarchyNodeItemInput
        }
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={tabIndex}
        data-name={node.getId()}
        disabled={isDisabled}
      >
        <HierarchyNodeItemLabel label={node.getLabel()} />
      </button>
    </span>
  );
};

HierarchyNodeItem.displayName = 'HierarchyNodeItem';

const getStyles: GetStylesFunction = (options) => ({
  dlHierarchyNodeItem: css`
    display: flex;
    justify-content: space-between;
    width: 100%;
  `,
  highlightedItem: css`
    ${mixins.dlButtonAsLinkDecorated};
    padding-bottom: 2px;
    word-break: break-all;
    width: 100%;
    display: flex;
    align-content: center;
    flex-wrap: wrap;

    outline: none;
    text-decoration: none;
    background-color: ${options?.theme?.palette.custom
      .dlHierarchyNodeItemFocus as string};
    color: ${options?.theme?.palette.custom
      .dlHierarchyNodeItemTextFocus as string};
    text-decoration: none;

    &:focus,
    &:active,
    &:hover {
      text-decoration: none;
      background-color: ${options?.theme?.palette.custom
        .dlHierarchyNodeItemFocusHover as string};
      color: ${options?.theme?.palette.custom
        .dlHierarchyNodeItemTextFocusHover as string};
    }
  `,
  dlHierarchyNodeItemInput: css`
    ${mixins.dlButtonAsLinkDecorated};
    padding-bottom: 2px;
    word-break: break-all;
    width: 100%;
    display: flex;
    align-content: center;
    flex-wrap: wrap;

    outline: none;
    text-decoration: none;
    color: ${options?.theme?.palette.custom.dlHierarchyNodeItemText as string};

    &:hover {
      background-color: ${options?.theme?.palette.custom
        .dlHierarchyNodeItemHover as string};
      text-decoration: none;
    }

    &:focus,
    &:active {
      background-color: ${options?.theme?.palette.custom
        .dlHierarchyNodeItemFocus as string};
      color: ${options?.theme?.palette.custom
        .dlHierarchyNodeItemTextFocus as string};
      text-decoration: none;
    }

    &:focus:hover {
      text-decoration: none;
      background-color: ${options?.theme?.palette.custom
        .dlHierarchyNodeItemFocusHover as string};
      color: ${options?.theme?.palette.custom
        .dlHierarchyNodeItemTextFocusHover as string};
    }
    :disabled {
      background-color: ${options?.theme?.palette.custom
        .dlHierarchyNodeItemDisabledBackground as string};
      color: ${options?.theme?.palette.custom
        .dlHierarchyNodeItemDisabled as string};
    }
  `,
});
